import { Combo } from 'types/combo';
import { Controller, useFormContext } from 'react-hook-form';
import Select, { createFilter } from 'react-select';
import { useMaterialUIController } from 'context';
import { useTranslation } from 'react-i18next';

type MultiSelectProps = {
    options: Combo[];
    onChange?: (values: Combo['value'][]) => void;
    name: string;
    placeholder?: string;
    disabled?: boolean;
    autoFocus?: boolean;
    closeMenuOnSelect?: boolean;
};

const MultiSelect = (props: MultiSelectProps) => {
    const { t } = useTranslation();
    const { control, setValue } = useFormContext();
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;

    return (
        <Controller
            control={control}
            name={props.name}
            render={({ field }) => {
                return (
                    <Select
                        isMulti
                        autoFocus={props.autoFocus}
                        closeMenuOnSelect={props.closeMenuOnSelect}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        options={props.options}
                        filterOption={createFilter({ stringify: (option) => option.label })}
                        isDisabled={props.disabled}
                        value={props.options.filter((option: Combo) =>
                            Array.isArray(field.value) ? field.value.includes(option.value) : false
                        )}
                        onChange={(options) => {
                            const values = options ? options.map((o: Combo) => o.value) : [];
                            field.onChange(values);
                            props.onChange ? props.onChange(values) : '';
                            setValue(props.name, values);
                        }}
                        placeholder={props.placeholder || t('general.selecione')}
                        styles={{
                            control: (base) => ({
                                ...base,
                                background: 'transparent',
                                border: darkMode ? '1px solid #405568' : 'lightgray solid 1px',
                                borderRadius: '5px',
                                minWidth: '155px',
                                minHeight: '43px',
                                color: darkMode ? '#ffffff' : '#000000',
                            }),
                            placeholder: (base) => ({
                                ...base,
                                fontSize: '14px',
                                color: darkMode ? '#b8c5d1' : '#6c757d',
                            }),
                            menuPortal: (base) => ({
                                ...base,
                                zIndex: 9999,
                            }),
                            menu: (base) => ({
                                ...base,
                                fontSize: '14px',
                                backgroundColor: darkMode ? '#1b2536' : '#FFFFFF',
                                color: darkMode ? '#ffffff' : '#000',
                                zIndex: 9999,
                            }),
                            option: (base, state) => ({
                                ...base,
                                backgroundColor: state.isFocused
                                    ? darkMode
                                        ? '#26324a'
                                        : '#e9ecef'
                                    : darkMode
                                    ? '#1b2536'
                                    : '#FFFFFF',
                                color: darkMode ? '#ffffff' : '#000000',
                                ':hover': {
                                    backgroundColor: darkMode ? '#26324a' : '#e9ecef',
                                },
                            }),
                            input: (base) => ({
                                ...base,
                                fontSize: '14px',
                                color: darkMode ? '#ffffff' : '#000000',
                            }),
                            multiValue: (base) => ({
                                ...base,
                                backgroundColor: darkMode ? '#26324a' : '#e9ecef',
                            }),
                            multiValueLabel: (base) => ({
                                ...base,
                                color: darkMode ? '#ffffff' : '#000000',
                            }),
                        }}
                    />
                );
            }}
        />
    );
};

export default MultiSelect;
